import React, { useState } from 'react';

import { Button, Group, Menu, Title, rem, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDeviceMobile, IconMail, IconPlus } from '@tabler/icons-react';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import { NotificationMessageTemplate } from 'types';

import DrawerEmailNotificationMessageTemplate from './components/DrawerEmailNotificationMessageTemplate';
import DrawerSMSNotificationMessageTemplate from './components/DrawerSMSNotificationMessageTemplate';
import NotificationMessageTemplatesTableContainer from './components/NotificationMessageTemplatesTableContainer';

const NotificationMessageTemplates = (): JSX.Element => {
  const theme = useMantineTheme();
  const { hasPerm } = useAuth();

  const flowsManagePerm = hasPerm('FlowMutate');

  const [tableData, setTableData] = useState<NotificationMessageTemplate[]>([]);

  const [selectedNotificationMessageTemplate, setSelectedNotificationMessageTemplate] =
    useState<NotificationMessageTemplate | null>(null);

  const [isSMSDrawerOpen, { toggle: handleSMSDrawerToggle }] = useDisclosure(false);
  const [isEmailDrawerOpen, { toggle: handleEmailDrawerToggle }] = useDisclosure(false);

  const handleCloseDrawer = (): void => {
    if (isSMSDrawerOpen) {
      handleSMSDrawerToggle();
    }
    if (isEmailDrawerOpen) {
      handleEmailDrawerToggle();
    }
  };

  const handleDrawerToggle = (notificationType: 'SMS' | 'Email'): void => {
    if (notificationType == 'SMS') {
      handleSMSDrawerToggle();
    }
    if (notificationType == 'Email') {
      handleEmailDrawerToggle();
    }
  };

  const handleCreateSMSTemplate = (): void => {
    setSelectedNotificationMessageTemplate(null);
    handleDrawerToggle('SMS');
  };
  const handleCreateEmailTemplate = (): void => {
    setSelectedNotificationMessageTemplate(null);
    handleDrawerToggle('Email');
  };

  return (
    <>
      <PageHeader>
        <Group position='apart' align='center' noWrap>
          <Title order={1} size='h1'>
            Notification Message Templates
          </Title>
          <Group>
            <Menu shadow='md' width={200}>
              <Menu.Target>
                <Button
                  leftIcon={<IconPlus size={theme.fontSizes.md} />}
                  disabled={!flowsManagePerm}
                  data-cy='createNotificationMessageTemplate'
                >
                  Template
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={handleCreateSMSTemplate}
                  icon={<IconDeviceMobile style={{ width: rem(14), height: rem(14) }} />}
                >
                  SMS
                </Menu.Item>
                <Menu.Item
                  onClick={handleCreateEmailTemplate}
                  icon={<IconMail style={{ width: rem(14), height: rem(14) }} />}
                >
                  Email
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </PageHeader>
      <NotificationMessageTemplatesTableContainer
        tableData={tableData}
        setTableData={setTableData}
        permission={flowsManagePerm}
        setSelectedNotificationMessageTemplate={setSelectedNotificationMessageTemplate}
        selectedNotificationMessageTemplate={selectedNotificationMessageTemplate}
        handleDrawerToggle={handleDrawerToggle}
      />
      <DrawerEmailNotificationMessageTemplate
        formId='drawer-edit-email-notification-message-template'
        opened={isEmailDrawerOpen}
        closeHandler={handleCloseDrawer}
        notificationMessageTemplateId={selectedNotificationMessageTemplate?.notificationMessageTemplateId}
        permission={flowsManagePerm}
      />
      <DrawerSMSNotificationMessageTemplate
        formId='drawer-edit-sms-notification-message-template'
        opened={isSMSDrawerOpen}
        closeHandler={handleCloseDrawer}
        notificationMessageTemplateId={selectedNotificationMessageTemplate?.notificationMessageTemplateId}
        permission={flowsManagePerm}
      />
    </>
  );
};

export default NotificationMessageTemplates;
